import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';

import * as actions from 'pkg/actions';
import * as api from 'pkg/api';
import * as endpoints from 'pkg/api/endpoints/auto';
import * as models from 'pkg/api/models';
import type {
	AccountClearFlags,
	AccountClearNotificationSettings,
	AccountFlags,
	AccountNotificationSettings,
} from 'pkg/api/models/account';
import { AccountRelationTypes } from 'pkg/api/models/account_relation';
import { create } from 'pkg/api/models/actions';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';

export interface CreateAccountResponse {
	authToken: string;
	orgToken: string;
	account: models.account.Account;
	childAccountId?: number;
	/** The account has to enter the code emailed to it; no tokens or account come along. */
	verificationRequired?: boolean;
}

interface PatchAccountPayload {
	firstName?: string;
	lastName?: string;
	email?: string;
	birthDate?: string;
	countryCode?: string;
	languageCode?: string;
	hourFormat?: '12h' | '24h';
	profileImage?: string;
	password?: string;
	passwordConfirm?: string;
	currentPassword?: string;
	flags?: AccountFlags[] | AccountClearFlags[];
	appleAuthCode?: string;
	notificationSettings?:
		| AccountNotificationSettings[]
		| AccountClearNotificationSettings[];
}

export async function patchAccount(
	accountId: number,
	payload: PatchAccountPayload,
	token?: string
): Promise<models.account.Account> {
	const headers: { [key: string]: string } = {};

	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const request = await sdk.patch(
		endpoints.Accounts.Update(accountId),
		{},
		payload,
		headers
	);

	const response = await request.json();

	if (!request.ok) {
		flashActions.show({ title: t(`Could not save!`) }, request.status);

		return null;
	}

	return response;
}

export const checkParentalConsent = async (): Promise<boolean> => {
	const req = await sdk.get(endpoints.Selfs.ShowSelf());

	const resp: {
		account: models.account.Account;
		organizations: models.group.OrganizationData[];
	} = await req.json();

	return !resp.account.needsParentalConsent;
};

export const setParentalConsent = async (): Promise<any> => {
	const req = await sdk.get(endpoints.Selfs.ShowSelf());

	const resp: {
		account: models.account.Account;
		organizations: models.group.OrganizationData[];
	} = await req.json();

	return !resp.account.needsParentalConsent;
};

export const requestParentalConsent = async (
	email: string
): Promise<Response> => {
	if (email.indexOf('@') === -1) {
		return;
	}

	const request = await sdk.post(
		endpoints.AccountTokens.CreateParentalConsentToken(),
		{},
		{ email }
	);

	return request;
};

export const grantAccountParentalConsent =
	(accountId: number) => async (): Promise<boolean> => {
		const request = await sdk.post(
			endpoints.Accounts.GrantParentalConsent(accountId)
		);

		if (!request.ok) return false;

		flashActions.show(
			{
				title: t(`Granted access`),
				message: t(`You granted your child access to 360Player.`),
			},
			request.status
		);

		return true;
	};

export const revokeAccountParentalConsent =
	(accountId: number) => async (): Promise<boolean> => {
		const request = await sdk.destroy(
			endpoints.Accounts.RemoveParentalConsent(accountId)
		);

		if (!request.ok) return false;

		flashActions.show(
			{
				title: t(`Removed access`),
				message: t(`Consent removed, your child can no longer use 360Player.`),
			},
			request.status
		);

		return true;
	};

export interface CreateChildAccountPayload {
	firstName: string;
	lastName: string;
	birthDate: string; // required in YYYY-MM-DD format
	countryCode: string;
	languageCode?: string;

	email?: string;
	password?: string;
}

export async function createChildAccount(
	accountId: number,
	payload: CreateChildAccountPayload
): Promise<[boolean, models.account.Account]> {
	const [request, response] = await create<
		CreateChildAccountPayload,
		models.account.Account
	>(endpoints.Accounts.CreateChildAccountForAccount(accountId), payload);

	if (!request.ok) {
		const c = crash();

		actions.flashes.show({
			title: c.title,
			message: c.description,
		});

		return [false, null];
	}

	return [true, response];
}

export async function swapRelationship(
	user: models.user.User,
	relatedUser: models.user.User
): Promise<boolean> {
	const [request] = await api.post(
		endpoints.Accounts.SwapRelation(user.accountId),
		{
			targetAccountID: relatedUser.accountId,
		}
	);

	if (request.ok) {
		flashActions.show(
			{
				title: t('Successfully saved!'),
			},
			request.status
		);
		return true;
	} else {
		flashActions.show(
			{
				title: t('Could not save!'),
			},
			request.status
		);
		return false;
	}
}

export async function removeRelationship(
	user: models.user.User,
	relatedUser: models.user.User
): Promise<boolean> {
	const [request] = await api.delete(
		endpoints.Accounts.DeleteRelation(user.accountId, relatedUser.accountId),
		{}
	);

	if (request.ok) {
		flashActions.show(
			{
				title: t('Successfully removed relationship!'),
			},
			request.status
		);
		return true;
	} else {
		flashActions.show(
			{
				title: t('Could not remove the relationship!'),
			},
			request.status
		);
		return false;
	}
}

export async function sendRelationshipInviteByUser(
	selectedUserAccountId: number,
	relatedUserAccountId: number,
	currentRelationshipType: AccountRelationTypes
): Promise<boolean> {
	const [request] = await api.post(
		endpoints.Accounts.CreateRelation(selectedUserAccountId),
		{
			targetAccountID: relatedUserAccountId,
			type: currentRelationshipType,
		}
	);

	if (request.ok) {
		flashActions.show(
			{
				title: t('Invitation sent!'),
			},
			request.status
		);

		return true;
	} else {
		flashActions.show(
			{
				title: t('Could not send invitation!'),
			},
			request.status
		);

		return false;
	}
}

export async function sendRelationshipInviteByEmail(
	email: string,
	relatedUserAccountId: number,
	currentRelationshipType: AccountRelationTypes
): Promise<boolean> {
	// AccountInvites only supports the "invite becomes legal guardian of
	// relatedUserAccountId" direction (a child-target invite). There's no
	// backend equivalent for the reverse (inviting someone by email to
	// become a new ward of relatedUserAccountId), so that direction still
	// sends no targets, same as before this fix.
	const targets =
		currentRelationshipType === AccountRelationTypes.LegalGuardian
			? [
					{
						type: models.accountInvite.TargetType.Child,
						targetId: relatedUserAccountId,
					},
				]
			: [];

	const [request] = await api.post(endpoints.AccountInvites.Index(), {
		email,
		targets,
	});

	if (request.ok) {
		flashActions.show(
			{
				title: t('Invitation sent!'),
			},
			request.status
		);

		return true;
	} else {
		flashActions.show(
			{
				title: t('Could not send invitation!'),
			},
			request.status
		);

		return false;
	}
}
